/**
 * Знаки марок для значка машины.
 *
 * Взяты из KEPwise — там лежат настоящие логотипы, и рисовать их заново
 * бессмысленно: с первой попытки вышли узнаваемые, но грубые фигуры, которые
 * рядом с настоящим знаком видно сразу. Взято же не как есть, а приведено под
 * наш размер.
 *
 * Что с ними сделано.
 *
 * Векторные (Mercedes, Volkswagen, Toyota, Nissan) — сняты хромовые градиенты.
 * Хром рассчитан на крупный размер: в кружке 25–27 пикселей переливы
 * схлопываются в серое пятно, и марки перестают отличаться друг от друга.
 * Вместо градиента стоит currentColor, поэтому знак сам темнеет на белом
 * маркере карты и светлеет на тёмной панели.
 *
 * Растровые (Ford, Fiat, Citroën, Opel, Renault, Peugeot, Maxus) — обрезаны по
 * знаку и сведены к 96 пикселям. Исходники весили до 435 КБ штука, и тринадцать
 * таких означали бы полтора мегабайта на каждое открытие; после обрезки —
 * от трёх до семи килобайт. У Citroën, Peugeot и Maxus отрезана подпись под
 * знаком: в кружке она нечитаема и превращает знак в грязь.
 *
 * Хранятся строками SVG, а не разметкой React: тот же знак нужен на карте, где
 * маркер собирается обычным DOM и React в нём не живёт. Две копии разошлись бы
 * при первой правке, поэтому источник один. Строки здесь — наши константы, не
 * чужой ввод: подставлять их в innerHTML безопасно. Имя машины, которое задаёт
 * человек, так вставлять нельзя, и оно так и не вставляется.
 */

export type ArtId =
  | 'mercedes'
  | 'man'
  | 'ford'
  | 'fiat'
  | 'vw'
  | 'renault'
  | 'citroen'
  | 'opel'
  | 'toyota'
  | 'nissan'
  | 'peugeot'
  | 'maxus'
  | 'volvo'
  | 'iveco'
  | 'daf'
  | 'scania'
  | 'isuzu'
  | 'dacia'

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif"

/** Растровый знак: файл лежит в public/brand и отдаётся отдельно от кода —
    так браузер возьмёт только те марки, которые есть в парке. */
const png = (name: string) =>
  `<img src="/brand/${name}.png" alt="" width="96" height="96" loading="lazy">`

/** Звезда в кольце. Контур из KEPwise, хром снят. */
const MERCEDES = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><path style="fill:currentColor" d="M 732.04995,56.71176 C 891.25911,140.47867 1000,307.49219 1000,500 1000,692.5078 891.25911,860.04162 732.04995,943.28824 l 0,-57.23205 C 792.92404,849.6358 844.43288,798.64724 881.89386,738.29344 l -149.84391,-60.87409 0,-92.61186 164.41207,128.51197 C 930.80125,649.84391 950.05203,577.52341 950.05203,500 c 0,-163.37149 -87.40895,-306.97191 -218.00208,-386.05619 l 0,-57.23205 z M 500.52029,0 c 83.24662,0 161.81062,20.29136 231.52966,56.71176 l 0,57.23205 C 668.05411,75.44225 593.13215,52.54943 514.04787,49.94797 l 57.23205,409.4693 160.77003,125.39022 0,92.61186 -232.57024,-94.17273 -231.00937,94.69302 0,-92.09157 161.81062,-126.4308 56.71176,-409.4693 c -80.12487,2.60146 -154.52654,25.49428 -218.52238,63.99584 l 0,-57.23205 C 337.66909,20.29136 416.75338,0 500.52029,0 l 0,0 z m 231.52966,943.28824 c -69.71904,36.4204 -148.28304,57.23206 -231.52966,57.23206 -83.76691,0 -162.8512,-20.81166 -232.04995,-56.71177 l 0,-57.75234 c 67.63788,41.10302 147.24246,64.51613 232.04995,64.51613 84.2872,0 163.37149,-23.41311 231.52966,-64.51613 l 0,57.23205 z m -463.57961,0.52029 C 108.74089,860.04162 0,692.5078 0,500 0,307.49219 108.74089,140.47867 268.47034,56.71176 l 0,57.23205 C 137.35692,193.02809 49.947971,336.10822 49.947971,500 c 0,77.52341 19.771072,150.88449 54.110299,214.36004 l 164.41207,-128.51197 0,92.09157 -149.32362,61.39439 c 37.46098,59.8335 88.44953,110.30177 149.32362,146.72216 l 0,57.75234 z" /></svg>`

/** V над W в круге. Контур из KEPwise, хром снят. */
const VW = `<svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><g transform="matrix(10.188387870788574, 0, 0, 10.188387870788574, -251.519936680809, -252.79260253906244)"><path d="M75,120.4c-24.9,0-45.3-20.5-45.3-45.4c0-5.6,1-10.9,2.9-15.9l26.5,53.3c0.3,0.7,0.8,1.3,1.6,1.3c0.8,0,1.3-0.6,1.6-1.3l12.2-27.3c0.1-0.3,0.3-0.6,0.6-0.6s0.4,0.3,0.6,0.6l12.2,27.3c0.3,0.7,0.8,1.3,1.6,1.3c0.8,0,1.3-0.6,1.6-1.3l26.5-53.3c1.9,5,2.9,10.3,2.9,15.9C120.3,99.9,99.9,120.4,75,120.4z M75,64.7c-0.3,0-0.4-0.3-0.6-0.6l-14.2-32c4.6-1.7,9.6-2.6,14.8-2.6c5.2,0,10.2,0.9,14.8,2.6l-14.2,32C75.4,64.5,75.3,64.7,75,64.7z M60.5,97.6c-0.3,0-0.4-0.3-0.6-0.6l-23-46.4c4.1-6.3,9.6-11.6,16.3-15.3l16.6,36.9C70,72.8,70.5,73,71,73h8c0.6,0,1-0.1,1.3-0.8l16.6-36.9c6.6,3.7,12.2,9,16.3,15.3L90,97c-0.1,0.3-0.3,0.6-0.6,0.6c-0.3,0-0.4-0.3-0.6-0.6l-8.7-19.8c-0.3-0.7-0.7-0.8-1.3-0.8h-8c-0.6,0-1,0.1-1.3,0.8L61.1,97C61,97.3,60.8,97.6,60.5,97.6z M75,125c27.7,0,50-22.3,50-50c0-27.7-22.3-50-50-50c-27.7,0-50,22.3-50,50C25,102.7,47.3,125,75,125z" style="fill:currentColor;"/></g></svg>`

/** Три пересекающихся овала. Контур из KEPwise, хром снят. */
const TOYOTA = `<svg viewBox="0 0 60 39" xmlns="http://www.w3.org/2000/svg"><path style=" stroke:none;fill-rule:nonzero;fill:currentColor;fill-opacity:1;" d="M 30 0.269531 C 13.585938 0.269531 0.277344 8.878906 0.277344 19.496094 C 0.277344 30.117188 13.585938 38.730469 30 38.730469 C 46.414062 38.730469 59.722656 30.121094 59.722656 19.496094 C 59.722656 8.871094 46.414062 0.269531 30 0.269531 Z M 30 30.476562 C 27.558594 30.476562 25.566406 25.667969 25.441406 19.617188 C 26.910156 19.746094 28.4375 19.816406 30 19.816406 C 31.5625 19.816406 33.089844 19.746094 34.558594 19.617188 C 34.433594 25.664062 32.441406 30.476562 30 30.476562 Z M 25.742188 14.8125 C 26.40625 10.535156 28.0625 7.5 30 7.5 C 31.9375 7.5 33.59375 10.535156 34.257812 14.8125 C 32.894531 14.933594 31.472656 15 30 15 C 28.527344 15 27.105469 14.933594 25.742188 14.8125 Z M 30 3.050781 C 26.8125 3.050781 24.09375 7.824219 23.089844 14.488281 C 17.046875 13.546875 12.828125 11.398438 12.828125 8.902344 C 12.828125 5.535156 20.515625 2.804688 30 2.804688 C 39.484375 2.804688 47.171875 5.535156 47.171875 8.902344 C 47.171875 11.398438 42.953125 13.546875 36.910156 14.488281 C 35.90625 7.824219 33.1875 3.050781 30 3.050781 Z M 4.539062 18.777344 C 4.539062 15.519531 5.792969 12.460938 7.976562 9.835938 C 7.953125 10.019531 7.9375 10.207031 7.9375 10.394531 C 7.9375 14.5 14.09375 18 22.683594 19.289062 C 22.675781 19.585938 22.675781 19.886719 22.675781 20.1875 C 22.675781 27.839844 24.820312 34.320312 27.785156 36.519531 C 14.757812 35.734375 4.539062 28.089844 4.539062 18.777344 Z M 32.214844 36.519531 C 35.179688 34.320312 37.324219 27.839844 37.324219 20.1875 C 37.324219 19.882812 37.320312 19.585938 37.316406 19.289062 C 45.902344 18 52.0625 14.503906 52.0625 10.394531 C 52.0625 10.207031 52.046875 10.019531 52.023438 9.835938 C 54.207031 12.46875 55.460938 15.519531 55.460938 18.777344 C 55.460938 28.089844 45.242188 35.734375 32.214844 36.519531 Z M 32.214844 36.519531 "/></svg>`

/** Кольцо с лентой поперёк. Контур из KEPwise, хром снят. */
const NISSAN = `<svg viewBox="0 0 264.58 221.38" xmlns="http://www.w3.org/2000/svg"><g transform="translate(170.24 5.0558)"><g id="Layer_1_1_" transform="matrix(.52497 0 0 .52497 -170.24 -5.0558)"><g id="Nissan_Badge_1_"><path class="st0" d="m293.7 227.7c-.4.1-2 .1-2.7.1h-51.6v12h52.5c.4 0 3.5 0 4.1-.1 10.7-1 15.6-9.9 15.6-17.7 0-8-5.1-16.6-14.8-17.5-1.9-.2-3.5-.2-4.2-.2h-34.3c-1.5 0-3.2-.1-3.8-.3-2.7-.7-3.7-3.1-3.7-5.1 0-1.8 1-4.2 3.8-5 .8-.2 1.7-.3 3.6-.3h49.5v-11.8h-50.3c-2.1 0-3.7.1-5 .3-8.6 1.2-14.6 8.1-14.6 16.9 0 7.2 4.5 15.6 14.4 17 1.8.2 4.3.2 5.4.2h33.4c.6 0 2.1 0 2.4.1 3.8.5 5.1 3.3 5.1 5.8 0 2.4-1.5 5-4.8 5.6z"/><path class="st0" d="m195.9 227.7c-.4.1-2 .1-2.6.1h-51.7v12h52.5c.4 0 3.5 0 4.1-.1 10.7-1 15.6-9.9 15.6-17.7 0-8-5.1-16.6-14.8-17.5-1.9-.2-3.5-.2-4.2-.2h-34.3c-1.5 0-3.2-.1-3.8-.3-2.7-.7-3.7-3.1-3.7-5.1 0-1.8 1-4.2 3.8-5 .8-.2 1.7-.3 3.6-.3h49.5v-11.8h-50.3c-2.1 0-3.7.1-5 .3-8.6 1.2-14.6 8.1-14.6 16.9 0 7.2 4.5 15.6 14.4 17 1.8.2 4.3.2 5.4.2h33.4c.6 0 2.1 0 2.4.1 3.8.5 5.1 3.3 5.1 5.8 0 2.4-1.4 5-4.8 5.6z"/><rect class="st0" x="101.7" y="181.4" width="13" height="58.7"/><polygon class="st0" points="73 240.1 73 181.4 60 181.4 60 225.2 16.7 181.4 0 181.4 0 240.1 13 240.1 13 196.1 56.6 240.1"/><polygon class="st0" points="491.1 181.4 491.1 225.2 447.8 181.4 431.1 181.4 431.1 240.1 444.1 240.1 444.1 196.1 487.6 240.1 504 240.1 504 181.4"/><path class="st0" d="m363.4 181.4-36.6 58.7h15.8l6.5-10.5h42.7l6.5 10.5h15.7l-36.6-58.7zm21.7 37.1h-29.3l14.7-23.6z"/><path class="st0" d="m72.3 148.1c26.2-76.5 98.4-127.9 179.8-127.9s153.7 51.4 179.8 127.9l.2.6h57.3v-6.9l-23.8-2.8c-14.7-1.7-17.8-8.2-21.8-16.4l-1-2c-34.4-73.2-109.3-120.6-190.7-120.6-81.5 0-156.3 47.4-190.7 120.8l-1 2c-4 8.2-7.1 14.7-21.8 16.4l-23.8 2.8v6.9h57.2z"/><path class="st0" d="m432.1 272.9-.2.6c-26.2 76.5-98.4 127.8-179.8 127.8s-153.7-51.4-179.8-127.9l-.2-.6h-57.2v6.9l23.8 2.8c14.7 1.7 17.8 8.2 21.8 16.4l1 2c34.4 73.4 109.3 120.8 190.7 120.8s156.3-47.4 190.7-120.7l1-2c4-8.2 7.1-14.7 21.8-16.4l23.8-2.8v-6.9z"/></g></g></g></svg>`

/**
 * Надпись вместо знака.
 *
 * У части марок логотип целиком состоит из названия — рисовать там нечего, и
 * обрубок вроде «SCA» хуже полного слова: он не значит ничего. Слово ставится
 * целиком и подгоняется по ширине кружка через textLength: браузер сам сожмёт
 * буквы ровно настолько, насколько надо, и длинное имя не вылезет за край.
 *
 * Кегль зависит от длины — чтобы буквы остались нормальных пропорций, а не
 * растянутыми или сплюснутыми.
 */
const SIZE_BY_LENGTH: Record<number, number> = { 2: 15, 3: 10.6, 4: 8, 5: 6.4, 6: 5.4 }

const word = (text: string) => {
  const size = SIZE_BY_LENGTH[text.length] ?? 5
  return `<svg viewBox="0 0 24 24"><text x="12" y="${12 + size * 0.36}" text-anchor="middle" textLength="20" lengthAdjust="spacingAndGlyphs" font-family="${FONT}" font-weight="800" font-size="${size}" fill="currentColor">${text}</text></svg>`
}

/** Знак Volvo: кольцо и стрелка вправо-вверх, знак железа. */
const VOLVO = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
<circle cx="10.3" cy="13.7" r="7.2"/><path d="M15.5 8.5 20.8 3.2"/><path d="M16.3 3.2h4.6v4.6"/></svg>`

/** Название марки для человека. Сервер отдаёт свой такой же список — за тем,
    чтобы они не разошлись, следит tests/test_brands.py. */
export const BRAND_NAMES: Record<ArtId, string> = {
  man: 'MAN',
  mercedes: 'Mercedes-Benz',
  fiat: 'Fiat',
  ford: 'Ford',
  vw: 'Volkswagen',
  renault: 'Renault',
  peugeot: 'Peugeot',
  citroen: 'Citroën',
  opel: 'Opel',
  toyota: 'Toyota',
  nissan: 'Nissan',
  volvo: 'Volvo',
  iveco: 'Iveco',
  maxus: 'Maxus',
  daf: 'DAF',
  scania: 'Scania',
  isuzu: 'Isuzu',
  dacia: 'Dacia',
}

/** Порядок в списке выбора: сначала то, что в парке. Тот же, что у сервера. */
export const BRANDS: ArtId[] = [
  'man', 'mercedes', 'fiat', 'ford', 'vw', 'renault', 'peugeot', 'citroen',
  'opel', 'toyota', 'nissan', 'volvo', 'iveco', 'maxus', 'daf', 'scania',
  'isuzu', 'dacia',
]

export const ART: Record<ArtId, string> = {
  mercedes: MERCEDES,
  man: word('MAN'),
  vw: VW,
  toyota: TOYOTA,
  nissan: NISSAN,
  ford: png('ford'),
  fiat: png('fiat'),
  citroen: png('citroen'),
  opel: png('opel'),
  renault: png('renault'),
  peugeot: png('peugeot'),
  maxus: png('maxus'),
  volvo: VOLVO,
  // У этих логотип и есть надпись: Iveco, DAF, Isuzu, Scania и Dacia
  // подписывают машины именно словом, а не фигурой.
  iveco: word('IVECO'),
  daf: word('DAF'),
  scania: word('SCANIA'),
  isuzu: word('ISUZU'),
  dacia: word('DACIA'),
}
